import { Contact } from "../pojo/Contact";
import { ContactRole, ContactRoleId } from "../services/organization.service";
import { SearchCriteriaService } from "../services/search/search-criteria/search.criteria.service";
import { S25Array } from "./core/s25.array";
import { S25Util } from "./s25-util";
import { S25ItemI } from "../pojo/S25ItemI";

type EditableContactOrg = Contact.DetailData["editableOrgs"][number];

export class ObjectDetailsUtil {
    /***********************
     * filterMasterDefinitions - uses searchCriteriaService to apply master definitions filtrations
     **************************/
    public static filterMasterDefinitions(
        searchCriteriaMethod: string,
        items: S25ItemI[],
        idProp?: string,
        args?: any[],
    ) {
        items = S25Util.array.forceArray(items);
        idProp = idProp || "itemId";

        const searchCriteriaService: any = SearchCriteriaService;
        return searchCriteriaService[searchCriteriaMethod](...(args ?? [])).then((catalog: S25ItemI[]) =>
            ObjectDetailsUtil.intersectWithMasterCatalog(items, catalog, idProp),
        );
    }

    public static intersectWithMasterCatalog(
        items: S25ItemI[],
        masterCatalog: S25ItemI[],
        idProp = "itemId",
        matchProps: string[] = ["itemId"],
        carryProps: string[] = ["sort_order"],
    ): S25ItemI[] {
        const normalizedItems = items.map((item: S25ItemI) => {
            item.itemId = item[idProp];
            return item;
        });
        const filteredItems = S25Util.array.shallowIntersection(normalizedItems, masterCatalog, matchProps, carryProps);
        return filteredItems.map((item) => {
            item[idProp] = item.itemId;
            return item;
        });
    }

    public static filterOrganizationRoleItems(roles: S25ItemI[]): Promise<S25ItemI[]> {
        return ObjectDetailsUtil.filterMasterDefinitions("getOrganizationRoles", roles, "itemId", [true]);
    }

    private static async allowedOrganizationRoleIds(distinctRoles: S25ItemI[]): Promise<Set<number>> {
        const visibleRoles = await ObjectDetailsUtil.filterOrganizationRoleItems(distinctRoles);
        return new Set(visibleRoles.map((role) => Number(role.itemId)));
    }

    private static rolePassesCatalogFilter(
        roleItemId: string | number | undefined,
        allowedRoleIds: Set<number>,
    ): boolean {
        const roleId = Number(roleItemId);
        return allowedRoleIds.has(roleId) || roleId === ContactRoleId.None;
    }

    public static organizationRolesForMultiselectSelection(roles: S25ItemI[] | undefined): S25ItemI[] {
        const allRoles = roles ?? [];
        if (allRoles.length <= 1) {
            return allRoles;
        }
        return allRoles.filter((role) => Number(role.itemId) !== ContactRoleId.None);
    }

    public static organizationRolesForReadOnlyDisplay(roles: S25ItemI[] | undefined): S25ItemI[] {
        const rolesWithDisplayNames = (roles ?? []).filter((role) => role?.itemName?.trim());
        if (rolesWithDisplayNames.length <= 1) {
            return rolesWithDisplayNames;
        }
        return rolesWithDisplayNames.filter((role) => Number(role.itemId) !== ContactRoleId.None);
    }

    public static async filterContactRoleAssignments(contacts: ContactRole[]): Promise<ContactRole[]> {
        if (!contacts?.length) {
            return contacts ?? [];
        }
        const distinctRoles = S25Array.uniqueByProp(
            contacts.map((contact) => contact.role).filter((role) => role?.itemId != null),
            "itemId",
        );
        const allowedRoleIds = await ObjectDetailsUtil.allowedOrganizationRoleIds(distinctRoles);
        const contactIdsWithVisibleAssignment = new Set<number>();
        for (const contact of contacts) {
            if (ObjectDetailsUtil.rolePassesCatalogFilter(contact.role?.itemId, allowedRoleIds)) {
                contactIdsWithVisibleAssignment.add(contact.itemId);
            }
        }
        const contactIdToFirstRow = new Map<number, ContactRole>();
        const contactIdToIsPrimary = new Map<number, boolean>();
        for (const contact of contacts) {
            if (!contactIdToFirstRow.has(contact.itemId)) {
                contactIdToFirstRow.set(contact.itemId, contact);
            }
            if (contact.isPrimary) {
                contactIdToIsPrimary.set(contact.itemId, true);
            }
        }
        const hiddenOnlyStubEmitted = new Set<number>();
        const result: ContactRole[] = [];
        for (const contact of contacts) {
            if (ObjectDetailsUtil.rolePassesCatalogFilter(contact.role?.itemId, allowedRoleIds)) {
                result.push(contact);
                continue;
            }
            if (contactIdsWithVisibleAssignment.has(contact.itemId)) {
                continue;
            }
            if (hiddenOnlyStubEmitted.has(contact.itemId)) {
                continue;
            }
            hiddenOnlyStubEmitted.add(contact.itemId);
            const firstRow = contactIdToFirstRow.get(contact.itemId);
            if (!firstRow) {
                continue;
            }
            result.push({
                itemName: firstRow.itemName,
                itemId: firstRow.itemId,
                itemTitle: firstRow.itemTitle,
                itemEmail: firstRow.itemEmail,
                isPrimary: Boolean(contactIdToIsPrimary.get(contact.itemId)),
                itemTypeId: firstRow.itemTypeId,
            });
        }
        return result;
    }

    public static async filterEditableOrgRoles(
        orgs: Contact.DetailData["editableOrgs"],
    ): Promise<Contact.DetailData["editableOrgs"]> {
        if (!orgs?.length) {
            return orgs ?? [];
        }
        const distinctRoles = S25Array.uniqueByProp(
            orgs.flatMap((org) => org.roles ?? []).filter((role) => role?.itemId != null),
            "itemId",
        );
        const allowedRoleIds = await ObjectDetailsUtil.allowedOrganizationRoleIds(distinctRoles);
        return orgs.map((org) => {
            const roles = org.roles ?? [];
            const filteredRoles = roles.filter((role) =>
                ObjectDetailsUtil.rolePassesCatalogFilter(role.itemId, allowedRoleIds),
            );
            return { ...org, roles: filteredRoles };
        });
    }
}
